import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { COLORS } from '@/shared/constants/colors';
import { files as fileHelpers, FilesNamespace } from '@/shared/helpers';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Spinner } from '@/shared/uikitV2/Spinner';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { makeInputAcceptFormatStr } from '@/shared/utils/makeInputAcceptFormatStr';
import { FileItemInfo } from '@/widgets/modals/containers/UploadFiles/types';

import { BrowseFiles } from './components/BrowseFiles';
import { FileList } from './components/FilesList';
import { BtnBlock, Container, Header, Info, List, SubInfo, Title } from './styled';

type UploaderWidgetProps = {
  onFilesSelect?: (files: FileItemInfo[]) => void;
  uploadedFiles: FileItemInfo[];
  collapsedItemsCount?: number;
  onFileClick?: (item: FileItemInfo) => void;
  onRemove?: (id?: string) => void;
  acceptFiles?: FilesNamespace;
  isLoading?: boolean;
};

export const UploaderWidget: BFC<UploaderWidgetProps> = ({
  onFilesSelect,
  uploadedFiles,
  collapsedItemsCount,
  onFileClick,
  onRemove,
  style,
  acceptFiles = 'assetPhotos',
  isLoading,
}) => {
  const { t } = useTranslation('widgets/uploaderWidget');
  const [listItems, setListItems] = useState<FileItemInfo[]>([]);
  const acceptKeys = fileHelpers
    .getAllowedMimeTypes(acceptFiles)
    .map((accept) => accept.replace('image/', ''));

  const onBrowseFilesSelect = useCallback(
    (pickedFiles: File[]) => {
      const mappedFiles = pickedFiles.map((file) => ({
        id: uuidv4(),
        file,
        isValidType: fileHelpers.isValidType(acceptFiles, file.type),
        isValidSize: fileHelpers.isValidSize(file.size),
      }));

      setListItems((prev) => {
        const collected = [...mappedFiles, ...prev];
        onFilesSelect?.(collected);

        return collected;
      });
    },
    [onFilesSelect, uploadedFiles],
  );

  const onRemoveFile = useCallback((id: string) => {
    onRemove?.(id);
    setListItems((prevList) => {
      const newList = prevList.filter((f) => f.id !== id);
      onFilesSelect?.(newList);

      return newList;
    });
  }, []);

  const isShowFileList = (!isLoading && uploadedFiles.length > 0) || listItems.length > 0;

  return (
    <Container style={style}>
      <Header>
        <Info>
          <Title style={{ color: COLORS.systemSky200 }} size={14} bold>
            <Icon name="upload-simple" size="s16" />
            {t('title')}
          </Title>
          <SubInfo>
            {makeInputAcceptFormatStr(acceptKeys, t)} {t('acceptFilesBase')}
          </SubInfo>
        </Info>
        <BtnBlock className="btn-block">
          {isLoading && <Spinner />}
          <BrowseFiles
            disabled={isLoading}
            acceptFiles={acceptFiles}
            style={{ height: 40, fontSize: 14, width: '100%' }}
            onFilesSelect={onBrowseFilesSelect}
          >
            {t('browseBtn')}
          </BrowseFiles>
        </BtnBlock>
      </Header>
      <List style={{ paddingBottom: !isShowFileList ? 12 : undefined }}>
        {isShowFileList && (
          <FileList
            items={[...listItems, ...(uploadedFiles ?? [])]}
            collapsedItemsCount={collapsedItemsCount}
            omItemRemove={onRemoveFile}
            onItemClick={onFileClick}
          />
        )}
      </List>
    </Container>
  );
};
